<template>
    <div
        :style="{ '--background-image': `url(${imageUrl})` }"
        class="relay-images"
        data-test-id="relay-images"
    >
        <div class="cover"></div>
        <span
            v-if="readyImages.length > 0"
            class="gallery-link"
            data-test-id="gallery-button"
            @click.prevent="activateGallery"
        ></span>
        <span
            v-if="readyImages.length > 0"
            class="image-count"
            data-test-id="relay-image-count"
            >{{ readyImages.length }} images</span
        >
        <!-- eslint-disable vue/no-v-model-argument -->
        <PrimeDialog
            v-model:visible="isGalleryActive"
            :dismissableMask="true"
            :draggable="false"
            :pt="dialogPtOptions"
            class="image-browser-dialog"
            data-test-id="image-gallery-dialog"
            modal
        >
            <!-- eslint-enable vue/no-v-model-argument -->
            <Galleria
                id="image-gallery"
                :circular="true"
                :fullScreen="false"
                :numVisible="9"
                :pt="galleryPtOptions"
                :responsiveOptions="[]"
                :showItemNavigators="true"
                :showThumbnails="false"
                :value="readyLoadedImages"
                :activeIndex="activeImageIndex"
                containerClass="image-browser"
                data-test-id="image-gallery"
            >
                <template #item="slotProps">
                    <img :src="slotProps.item.large_url" />
                </template>
            </Galleria>
        </PrimeDialog>
    </div>
</template>

<script>
import Galleria from 'primevue/galleria'
import Dialog from 'primevue/dialog'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import relayTypes, { byValue } from '@/constants/relay-types'

export default {
    name: 'RelayImages',
    components: {
        Galleria,
        PrimeDialog: Dialog,
    },
    mixins: [needsOrganizationSlug],

    props: {
        images: {
            type: Array,
            default: () => [],
        },
        relayId: {
            type: String,
            required: true,
        },
        relayType: {
            type: String,
            default: relayTypes.EDGE.type,
        },
    },

    data() {
        return {
            isGalleryActive: false,
            loadedImages: [],
            galleryPtOptions: {
                nextButton: { 'data-test-id': 'image-gallery-next-button' },
                prevButton: { 'data-test-id': 'image-gallery-prev-button' },
                item: { 'data-test-id': 'image-gallery-image' },
            },
            dialogPtOptions: {
                headerActions: {
                    'data-test-id': 'image-gallery-header-actions',
                },
            },
            activeImageIndex: 0,
        }
    },

    computed: {
        imageUrl() {
            let imagePath = this.readyImages.find((image) => image.is_cover_image)?.thumbnail_url

            if (!imagePath) {
                imagePath = this.readyImages?.[0]?.thumbnail_url
            }

            if (!imagePath) {
                const relayType = byValue(this.relayType)

                // Default to EDGE relay image
                imagePath = relayType ? relayType.image : relayTypes.EDGE.image
            }

            return imagePath
        },
        readyImages() {
            return this.images.filter((image) => {
                return image.status === 'ready'
            })
        },
        readyLoadedImages() {
            return this.loadedImages.filter((image) => {
                return image.status === 'ready'
            })
        },
    },

    methods: {
        activateGallery() {
            const coverImageIndex = this.readyImages.findIndex((image) => image.is_cover_image)
            this.activeImageIndex = coverImageIndex === -1 ? 0 : coverImageIndex
            this.isGalleryActive = true
            this.$http.get(`/${this.organizationSlug}/relays/${this.relayId}/images`).then(({ data }) => {
                this.loadedImages = data
            })
        },
    },
}
</script>

<style lang="scss">
.image-browser-dialog.p-dialog {
    width: 685px;
    height: 446px;
    background-color: #212629;
    border: 1px solid #43474a;

    .p-dialog-header {
        padding: 0;
        justify-content: right;

        .p-button-secondary.p-dialog-close-button,
        .p-button-secondary.p-dialog-close-button:hover {
            outline: none;
            box-shadow: none;
            background: none;
        }
    }

    .p-dialog-content {
        padding: 0;

        .image-browser {
            border: 0;
            height: 363px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            --p-galleria-nav-button-gutter: 0;

            .p-galleria-nav-button,
            .p-galleria-nav-button {
                outline: none;
                box-shadow: none;
                background: none;
            }

            .p-galleria-item img {
                max-height: 363px;
                max-width: 603px;
                display: block;
            }
        }
    }
}

.relay-images {
    position: relative;
    border-radius: 8px;
    width: 230px;
    height: 110px;
    background-image: var(--background-image);
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center center;

    .cover {
        width: 100%;
        height: 100%;
        position: absolute;
        z-index: 1;
        background-color: black;
        border-radius: 8px;
        opacity: 0.4;
    }

    .gallery-link {
        display: block;
        width: 26px;
        height: 26px;
        cursor: pointer;
        background-image: url('@images/relays/image-gallery-icon.svg');
        background-repeat: no-repeat;
        position: absolute;
        right: 6px;
        bottom: 6px;
        z-index: 2;
    }

    .image-count {
        font-size: 11px;
        position: absolute;
        left: 16px;
        bottom: 16px;
        z-index: 2;
    }
}
</style>
